import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { signOut } from '@aws-amplify/auth';
import Sidebar from './Sidebar';
import { FaBars, FaUserCircle, FaSignOutAlt, FaCalendarAlt, FaCog, FaQuestionCircle } from 'react-icons/fa';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Toaster } from '../../components/ui/sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

const Layout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isHomepage = location.pathname === '/homepage' || location.pathname === '/';

    // User Role Olakha
    let role = null;
    if (user) {
        if (user.groups?.includes('admin') || user.role === 'admin') role = 'admin';
        else if (user.groups?.includes('owner') || user.role === 'owner') role = 'owner';
        else role = 'student';
    }

    const handleLogout = async () => {
        try {
            if (logout) {
                await logout();
            } else {
                await signOut();
            }
            navigate('/login');
        } catch (error) {
            navigate('/login');
        }
    };

    // Header cha title dynamically page nusar set kara
    let headerTitle = "Dorm & Dish";
    if (location.pathname.includes('/create-room')) headerTitle = "Create a New Room Listing";
    if (location.pathname.includes('/create-mess')) headerTitle = "Create a New Mess Listing";
    if (location.pathname.startsWith('/admin')) headerTitle = "Admin Panel";
    if (location.pathname === '/dashboard') headerTitle = "My Bookings";

    // Get user name from attributes (if available)
    const givenName = user?.attributes?.given_name || '';
    const familyName = user?.attributes?.family_name || '';
    const userName = (givenName && familyName)
        ? `${givenName} ${familyName}`
        : (user?.attributes?.name || user?.username || "User");

    const userRole = role?.charAt(0).toUpperCase() + role?.slice(1) || "Guest";

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Reserve space for absolute header — this ensures consistent spacing across ALL pages */}
            <div className="h-16" aria-hidden="true"></div>

            {/* Header - Globally Transparent & Absolute */}
            <header className={cn(
                "fixed top-0 left-0 right-0 bg-[#327b58] p-3 flex justify-between items-center z-40 transition-all duration-300",
                isHomepage ? "text-white/90" : "text-white"
            )}>
                <div className="flex items-center gap-4 justify-center">
                    {/* MENU BUTTON - TRIGGERS SHEET */}
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                    "transition-colors duration-200",
                                    isHomepage ? "text-white hover:bg-white/10" : "text-white hover:bg-white/10"
                                )}
                            >
                                <FaBars className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="p-0 w-[280px] bg-sidebar-dark border-r-0 text-white">
                            <Sidebar userRole={role} handleLogout={handleLogout} isSheet={true} />
                        </SheetContent>
                    </Sheet>

                    {/* Header Title or Logo */}
                    {!isHomepage && <h1 className="text-xl font-semibold hidden md:block">{headerTitle}</h1>}

                    {/* Navigation Menu (Home / About / Explore) */}
                    <div className="hidden md:flex items-center gap-2 flex-1 justify-center">

                        <Button
                            asChild
                            variant={(location.pathname === '/' || location.pathname.startsWith('/homepage')) ? "nav-active" : "nav-link"}
                            size="nav"
                        >
                            <Link to="/homepage">Home</Link>
                        </Button>

                        <Button
                            asChild
                            variant={location.pathname.startsWith('/rooms') ? "nav-active" : "nav-link"}
                            size="nav"
                        >
                            <Link to="/rooms">Rooms</Link>
                        </Button>

                        <Button
                            asChild
                            variant={location.pathname.startsWith('/mess') ? "nav-active" : "nav-link"}
                            size="nav"
                        >
                            <Link to="/mess">Mess</Link>
                        </Button>

                        <Button
                            asChild
                            variant={location.pathname.startsWith('/about') ? "nav-active" : "nav-link"}
                            size="nav"
                        >
                            <Link to="/about">About</Link>
                        </Button>

                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {user ? (
                        <>
                            {/* User Profile Dropdown */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="relative h-10 w-10 rounded-full p-0"
                                    >
                                        <div className="absolute top-0 right-0 h-2 w-2 rounded-full bg-green-500" />
                                        <Avatar className="h-10 w-10 border-2 border-white/20">
                                            <AvatarImage src={user?.attributes?.picture} alt={userName} />
                                            <AvatarFallback className="bg-green-100 text-green-800">
                                                {userName.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-80" align="end" forceMount>
                                    {/* User Header */}
                                    <div className="flex items-center p-4">
                                        <div className="flex-shrink-0">
                                            <Avatar className="h-12 w-12">
                                                <AvatarImage src={user?.attributes?.picture} alt={userName} />
                                                <AvatarFallback className="bg-green-100 text-green-800">
                                                    {userName.charAt(0).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                        </div>
                                        <div className="ml-3">
                                            <p className="font-medium">{userName}</p>
                                            <p className="text-xs text-gray-500">{userRole} Member</p>
                                            <Link to="/profile/view" className="text-green-600 text-xs">View Profile →</Link>
                                        </div>
                                    </div>
                                    <DropdownMenuSeparator />

                                    {/* Menu Items */}
                                    <DropdownMenuItem asChild>
                                        <Link to="/profile/view" className="flex items-center w-full">
                                            <div className="w-5 h-5 mr-2">
                                                <FaUserCircle className="text-gray-500" />
                                            </div>
                                            <span>My Profile</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    {role === 'student' && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/student/bookings" className="flex items-center w-full">
                                                <div className="w-5 h-5 mr-2">
                                                    <FaCalendarAlt className="text-gray-500" />
                                                </div>
                                                <span>My Bookings</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem asChild>
                                        <Link to="/settings" className="flex items-center w-full">
                                            <div className="w-5 h-5 mr-2">
                                                <FaCog className="text-gray-500" />
                                            </div>
                                            <span>Settings</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />

                                    {/* Help & Logout */}
                                    <DropdownMenuItem asChild>
                                        <Link to="/help" className="flex items-center w-full">
                                            <div className="w-5 h-5 mr-2">
                                                <FaQuestionCircle className="text-gray-500" />
                                            </div>
                                            <span>Help & Support</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                                        <div className="flex items-center w-full">
                                            <div className="w-5 h-5 mr-2">
                                                <FaSignOutAlt className="text-red-600" />
                                            </div>
                                            <span>Log Out</span>
                                        </div>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className={cn("text-sm font-medium transition-colors duration-200", isHomepage ? "text-white/80 hover:text-white" : "text-gray-600 hover:text-gray-900")}>
                                Login / Signup
                            </Link>
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-600 text-white transition-colors duration-200 hidden sm:inline-block">
                                GUEST
                            </span>
                        </>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1">
                <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="h-full"
                >
                    <Outlet />
                </motion.div>
            </main>

            {/* ===== GLOBAL TOASTER COMPONENT ADDED HERE ===== */}
            <Toaster />
        </div>
    );
};

export default Layout;